import { memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { messagePropType } from "app/utils/propTypes";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import CustomFormattedMessageContainer, {
	DISPLAY_OPTIONS,
} from "app/pages/.shared/CustomFormattedMessage/CustomFormattedMessageContainer";
import Paragraphs from "app/pages/.shared/Paragraphs";
import Tag, { TAG_SIZES } from "app/components/ui/Tag/Tag";
import { ButtonLink } from "app/components/ui/ButtonLink/ButtonLink";
import SmartDPMarketingHeroImageContainer from "app/pages/SmartDP/SmartDPMarketingHeroImage/SmartDPMarketingHeroImageContainer";
import "./MerchandisingHero.scss";

const IMG_SIZES = {
	medium: { width: 978 },
	small: { width: 768 },
	large: { width: 1847 },
	xlarge: { width: 1847 },
};

// TODO: hardcoded values for the POC, to be replaced by the merchandising data
const HARDCODED_PRICE_TAG = "DÈS 899 € /PERS.";
const HARDCODED_CONDITIONS_URL = "#";

const MerchandisingHero = ({
	title,
	subtitle,
	description,
	lowestPrice,
	hideLowestPrice = false,
	imageUrl,
	imageThumbnail,
	imageAlt,
	children,
}) => {
	const hasEdito = Boolean(title || subtitle || description);
	const hasSearchForm = Boolean(children);
	const templateDisplayOptions = { [DISPLAY_OPTIONS.CALL_PRICE]: !hideLowestPrice };

	return (
		<div
			className={classNames("merchandising-hero", {
				"merchandising-hero--without-edito": !hasEdito,
				"merchandising-hero--with-search-form": hasSearchForm,
			})}
		>
			<div className="merchandising-hero__banner">
				<div className="merchandising-hero__image" data-testid="main-picture">
					<SmartDPMarketingHeroImageContainer
						heroImageMobileThumbnail={imageThumbnail}
						heroImageDesktopThumbnail={imageThumbnail}
						heroImageMobile={imageUrl}
						heroImageDesktop={imageUrl}
						backgroundPosition="center"
						sizes={IMG_SIZES}
						alt={imageAlt}
					/>
				</div>

				{hasEdito && (
					<div
						className="merchandising-hero__edito"
						data-testid="merchandising-hero-edito"
					>
						<div className="merchandising-hero__heading">
							{title?.includes("aurice") && (
								<Tag
									size={TAG_SIZES.LARGE}
									isBold
									className="merchandising-hero__price-tag"
									data-testid="merchandising-hero-price-tag"
								>
									{HARDCODED_PRICE_TAG}
								</Tag>
							)}
							{(title || subtitle) && (
								<div className="merchandising-hero__title-group">
									{title && (
										<Typography
											variant={TYPOGRAPHY_VARIANTS.XL6}
											isBold
											component="h1"
											className="merchandising-hero__title"
										>
											<CustomFormattedMessageContainer
												displayOptions={templateDisplayOptions}
												message={title}
												lowestPrice={lowestPrice}
												enableHTMLFormatting
											/>
										</Typography>
									)}
									{subtitle && (
										<Typography
											variant={TYPOGRAPHY_VARIANTS.XL3}
											variantMD={TYPOGRAPHY_VARIANTS.XL2}
											isBold
											className="merchandising-hero__subtitle"
										>
											<CustomFormattedMessageContainer
												displayOptions={templateDisplayOptions}
												message={subtitle}
												lowestPrice={lowestPrice}
												enableHTMLFormatting
											/>
										</Typography>
									)}
								</div>
							)}
						</div>

						<div className="merchandising-hero__content">
							{description && (
								<Typography
									variant={TYPOGRAPHY_VARIANTS.REGULAR}
									variantMD={TYPOGRAPHY_VARIANTS.LARGE}
									className="merchandising-hero__description"
									data-testid="listing-introduction"
								>
									<Paragraphs paragraphs={description} enableHTMLFormatting />
								</Typography>
							)}
							{title?.includes("aurice") && (
								<ButtonLink
									variant="borderless"
									size="md"
									to={HARDCODED_CONDITIONS_URL}
									className="merchandising-hero__conditions"
									data-testid="merchandising-hero-conditions"
								>
									Voir conditions
								</ButtonLink>
							)}
						</div>
					</div>
				)}
			</div>

			{hasSearchForm && <div className="merchandising-hero__search-form">{children}</div>}
		</div>
	);
};

MerchandisingHero.propTypes = {
	title: messagePropType,
	subtitle: messagePropType,
	description: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
	lowestPrice: PropTypes.number,
	hideLowestPrice: PropTypes.bool,
	imageUrl: PropTypes.string,
	imageThumbnail: PropTypes.string,
	imageAlt: PropTypes.string,
};

export default memo(MerchandisingHero);
